import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  acknowledgeRentalWarning,
  getRental,
  getRentalAcceptedSnapshots,
  getRentalAgreementSummary,
  getRentalWarnings,
} from "../../api/workspaceRentals";
import { getPublicWorkspace } from "../../api/workspaceMarketplace";
import AcceptedTermsSummaryPanel from "../../components/rentals/AcceptedTermsSummaryPanel";
import RenterAcceptanceSummaryCard from "../../components/rentals/RenterAcceptanceSummaryCard";
import RenterWarningsPanel from "../../components/rentals/RenterWarningsPanel";
import { useAuth } from "../../context/AuthContext";
import MarketplaceShell from "./MarketplaceShell";

function formatAcceptedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function buildFinanceSummary(rental, latestSnapshot) {
  const financeState = latestSnapshot?.snapshot_json?.finance_state || {};
  return {
    currency: rental?.currency || financeState.currency || "cad",
    depositAmount:
      financeState.deposit_amount ??
      financeState.depositAmount ??
      latestSnapshot?.snapshot_json?.deposit_amount ??
      null,
    dueNowAmount:
      financeState.amount_due_now ??
      financeState.due_now ??
      latestSnapshot?.snapshot_json?.amount_due_now ??
      rental?.total_amount ??
      null,
    dueLaterAmount:
      financeState.amount_due_later ??
      financeState.due_later ??
      latestSnapshot?.snapshot_json?.amount_due_later ??
      0,
    paymentSummary: financeState.payment_timing || "",
  };
}

export default function MarketplaceRentalSummaryPage() {
  const { rentalId } = useParams();
  const { token } = useAuth();
  const [rental, setRental] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [agreementSummary, setAgreementSummary] = useState(null);
  const [acceptedSummary, setAcceptedSummary] = useState(null);
  const [warningsSummary, setWarningsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    if (!token || !rentalId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      getRental(rentalId, token),
      getRentalAgreementSummary(rentalId, token),
      getRentalAcceptedSnapshots(rentalId, token),
      getRentalWarnings(rentalId, token),
    ])
      .then(async ([rentalRow, agreementRow, acceptedRow, warningRow]) => {
        if (!active) return;
        setRental(rentalRow || null);
        setAgreementSummary(agreementRow || null);
        setAcceptedSummary(acceptedRow || null);
        setWarningsSummary(warningRow || null);
        if (rentalRow?.workspace_id) {
          try {
            const workspaceRow = await getPublicWorkspace(rentalRow.workspace_id, token);
            if (active) setWorkspace(workspaceRow || null);
          } catch (_) {
            if (active) setWorkspace(null);
          }
        }
      })
      .catch((err) => {
        if (active) {
          setError(err.message || "Unable to load rental summary.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [rentalId, token]);

  const latestAcceptance = acceptedSummary?.acceptances?.[0] || agreementSummary?.latest_acceptance || null;
  const latestSnapshot = acceptedSummary?.snapshots?.[0] || null;
  const financeSummary = useMemo(
    () => buildFinanceSummary(rental, latestSnapshot),
    [latestSnapshot, rental]
  );

  const handleAcknowledgeWarnings = async () => {
    if (!token || !rentalId) return;
    setSaving(true);
    setError("");
    setFlash("");
    try {
      const response = await acknowledgeRentalWarning(
        rentalId,
        { note: "Renter reviewed current accepted summary notices." },
        token
      );
      setWarningsSummary(response || null);
      setFlash("Current notices acknowledged.");
    } catch (err) {
      setError(err.message || "Unable to acknowledge current notices.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MarketplaceShell>
      <section className="space-y-6">
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="bg-[#2f3566] px-6 py-7 text-white">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-200">
              Canonical Rental Summary
            </p>
            <h1 className="mt-3 text-3xl font-semibold">
              {workspace?.booth_name || workspace?.name || `Rental #${rentalId}`}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-200 md:text-base">
              Review the arrangement, agreement version, accepted notices, and payment summary
              recorded for this MyBoothShare rental.
            </p>
          </div>
          <div className="space-y-6 px-6 py-6">
            {loading ? <p className="text-sm text-slate-600">Loading rental summary...</p> : null}
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {flash ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {flash}
              </div>
            ) : null}

            <div className="grid gap-6 lg:grid-cols-[0.95fr,1.05fr]">
              <RenterAcceptanceSummaryCard
                workspaceName={workspace?.booth_name || workspace?.name}
                workspaceType={workspace?.workspace_type}
                occupancyMode={rental?.occupancy_mode}
                clientOwnershipMode={rental?.client_ownership_mode}
                agreementVersion={latestAcceptance?.accepted_version || agreementSummary?.current_template?.version}
                depositAmount={financeSummary.depositAmount}
                dueNowAmount={financeSummary.dueNowAmount}
                dueLaterAmount={financeSummary.dueLaterAmount}
                currency={financeSummary.currency}
                warningCount={(warningsSummary?.warnings || []).length}
                acceptedAt={formatAcceptedAt(latestAcceptance?.accepted_at)}
                rentalId={rental?.id}
              />

              <AcceptedTermsSummaryPanel
                occupancyMode={rental?.occupancy_mode}
                clientOwnershipMode={rental?.client_ownership_mode}
                agreementVersion={latestAcceptance?.accepted_version || agreementSummary?.current_template?.version}
                acceptedAt={latestAcceptance?.accepted_at}
                warningCount={(warningsSummary?.warnings || []).length}
                warningAcknowledged={Boolean(warningsSummary?.latest_acknowledgement?.current)}
                paymentSummary={financeSummary.paymentSummary}
              />
            </div>

            <RenterWarningsPanel
              title="Important Notices"
              warnings={warningsSummary?.warnings || []}
              strictModeEnabled={Boolean(warningsSummary?.strict_mode_enabled)}
              agreementCompletionBlocked={Boolean(warningsSummary?.agreement_completion_blocked)}
              acknowledgementRequired={Boolean((warningsSummary?.warnings || []).length)}
            />

            {(acceptedSummary?.snapshots || []).length ? (
              <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
                  Accepted Snapshot History
                </p>
                <div className="mt-4 grid gap-3">
                  {(acceptedSummary?.snapshots || []).map((snapshot) => (
                    <article
                      key={snapshot.id}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600"
                    >
                      <div className="font-semibold text-slate-900">Snapshot #{snapshot.id}</div>
                      <div className="mt-2">
                        Operating relationship: {snapshot.occupancy_mode || "Not set"}
                      </div>
                      <div className="mt-1">
                        Client ownership: {snapshot.client_ownership_mode || "Not set"}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}

            <div className="flex flex-wrap gap-3">
              {(warningsSummary?.warnings || []).length ? (
                <button
                  type="button"
                  onClick={handleAcknowledgeWarnings}
                  disabled={saving}
                  className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Acknowledge Current Notices"}
                </button>
              ) : null}
              <Link
                to="/account/rentals"
                className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-slate-400"
              >
                Back to My Rentals
              </Link>
            </div>
          </div>
        </article>
      </section>
    </MarketplaceShell>
  );
}
